import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { AlertTriangle } from 'lucide-react';
import { tenantSelector } from '../../../selector/authenticationSelector';
import { makeAssetDetailsUrl } from '../../../routes';
import { tableCellClass, tableHeaderCellClass } from '../../../components/TableCard';
import OrgDetailSection from './OrgDetailSection';
import {
    OrganizationRoomGrant,
    SpaceAgreement,
} from '../../../api/organization/organizationApiTypes';
import {
    useCancelSpaceAgreementMutation,
    useCreateSpaceAgreementMutation,
    useGetOrganizationRoomAccessQuery,
    useGetOrganizationSpaceAgreementsQuery,
    useUpdateSpaceAgreementMutation,
} from '../../../api/organization/organizationApiSlice';
import {
    agreementEndedState,
    agreementLifecycleOf,
    endedAgreementsStillHoldingRooms,
    isAgreementBackingBenefits,
    isDedicatedWithoutAgreement,
} from '../spaceAgreementStatus';
import CustomButton from '../../../components/CustomButton';
import RowActionsMenu from '../../../components/RowActionsMenu';
import OrgAgreementForm, {
    AgreementFormValue,
    agreementFormToBody,
    agreementToForm,
    emptyAgreementForm,
} from './OrgAgreementForm';
import { formatDate } from '../../../utils';
import OrgRoomAssignmentEditor from './OrgRoomAssignmentEditor';
import OrgAllocationWindows from '../allocationWindows/OrgAllocationWindows';

interface OrgRoomAccessSectionProps {
    organizationId: string;
}

/**
 * Rooms & agreements (#2144, ADR-0016 Amendment 5).
 *
 * Rooms are ASSIGNED here since option C (docs/plans/coverage-per-room-assignment.md):
 * "Räume zuordnen" opens one editor for this organization's whole room list —
 * which rooms, shared or dedicated use, and which contract covers each room or
 * "access only". The object page only shows the result.
 *
 * The distinction this screen exists to make visible: a **shared** room is one
 * this organization may book alongside others, contended per booking. A
 * **dedicated** room *is* theirs under a space agreement — rent, not capacity.
 * Before Amendment 5 the two were stored identically and could only be told
 * apart by counting grant rows, which stopped being true the moment a second
 * organization was added.
 */
const OrgRoomAccessSection: React.FC<OrgRoomAccessSectionProps> = ({ organizationId }) => {
    const { t, i18n } = useTranslation();
    const language: 'de' | 'en' = i18n.language?.startsWith('de') ? 'de' : 'en';

    // skip until the tenant is restored — hard-reload race with the tenant
    // cache reset middleware (see OrganizationsTab).
    const tenant = useSelector(tenantSelector);
    const { data: access, isLoading: accessLoading, isError: accessError } =
        useGetOrganizationRoomAccessQuery({ organizationId }, { skip: !tenant });
    const { data: agreementList, isLoading: agreementsLoading, isError: agreementsError } =
        useGetOrganizationSpaceAgreementsQuery({ organizationId }, { skip: !tenant });

    const grants: OrganizationRoomGrant[] = access?.items ?? [];
    const agreements: SpaceAgreement[] = agreementList?.items ?? [];

    const naLabel = t('common.not_applicable');
    const roomName = (grant: OrganizationRoomGrant) => grant.groupName?.trim() || naLabel;

    // #2154 item 3: both halves of this warning are now the server's derived
    // fields, so the banner cannot disagree with the rows it sits above.
    const needsAttention = endedAgreementsStillHoldingRooms(agreements);

    const [assigning, setAssigning] = React.useState(false);

    const coverageLabel = (grant: OrganizationRoomGrant): string => {
        if (grant.spaceAgreementId === null) return t('organization.rooms.coverage.access_only');
        const match = agreements.find((agreement) => agreement.id === grant.spaceAgreementId);
        if (!match) return t('organization.rooms.agreement_archived');
        // A shared room keeps its link after the contract ends (plan §5), so
        // the label says whether the contract still covers it — the same rule
        // the server applies to bookings.
        return isAgreementBackingBenefits(match)
            ? t('organization.rooms.coverage.by', { name: match.name })
            : t('organization.rooms.coverage.ended', { name: match.name });
    };

    // #2165 4.1 - cancel is the action on a live customer; archive is not.
    // It opens against ONE agreement so the operator sees which rooms it
    // holds, rather than deciding from a row menu with no context.
    const [cancelAgreement, { isLoading: isCancelling }] = useCancelSpaceAgreementMutation();
    const [cancelTarget, setCancelTarget] = React.useState<SpaceAgreement | null>(null);
    const [endDate, setEndDate] = React.useState('');
    const [cancelError, setCancelError] = React.useState<string | null>(null);

    const openCancel = (agreement: SpaceAgreement) => {
        setCancelError(null);
        // Pre-filled from the term's own end date where there is one:
        // cancelling "as agreed" is the common case. It still has to be
        // confirmed - the field is required, never defaulted on submit.
        setEndDate(agreement.expiryDate ? agreement.expiryDate.slice(0, 10) : '');
        setCancelTarget(agreement);
    };

    const submitCancel = async () => {
        if (!cancelTarget || !endDate) return;
        setCancelError(null);
        try {
            await cancelAgreement({ agreementId: cancelTarget.id, endDate }).unwrap();
            setCancelTarget(null);
        } catch (err) {
            // The API owns the rules - a past date, a date before the term
            // started - so its message is shown rather than re-derived here.
            const e = err as { data?: { message?: string } };
            setCancelError(e?.data?.message ?? t('organization.agreements.cancel_error'));
        }
    };

    // #2165 3 - record a contract concluded elsewhere. The form captures the
    // contract's IDENTITY and term; it is not a contract editor.
    const [createAgreement, { isLoading: isCreating }] = useCreateSpaceAgreementMutation();
    const [updateAgreement, { isLoading: isUpdating }] = useUpdateSpaceAgreementMutation();
    const [formValue, setFormValue] = React.useState<AgreementFormValue | null>(null);
    const [editTarget, setEditTarget] = React.useState<SpaceAgreement | null>(null);
    const [formError, setFormError] = React.useState<string | null>(null);
    const [showValidation, setShowValidation] = React.useState(false);

    const openCreate = () => {
        setFormError(null);
        setShowValidation(false);
        setEditTarget(null);
        setFormValue(emptyAgreementForm());
    };

    const openEdit = (agreement: SpaceAgreement) => {
        setFormError(null);
        setShowValidation(false);
        setEditTarget(agreement);
        setFormValue(agreementToForm(agreement));
    };

    const closeForm = () => {
        setFormValue(null);
        setEditTarget(null);
        setFormError(null);
        setShowValidation(false);
    };

    const submitForm = async () => {
        if (!formValue) return;
        const body = agreementFormToBody(formValue);
        if (!body) {
            // Incomplete rather than rejected: the missing pieces are marked in
            // place, so the operator is not sent to read an error to find out
            // which field it was.
            setShowValidation(true);
            return;
        }
        setFormError(null);
        try {
            if (editTarget) {
                await updateAgreement({
                    agreementId: editTarget.id, organizationId, body,
                }).unwrap();
            } else {
                await createAgreement({ organizationId, body }).unwrap();
            }
            closeForm();
        } catch (err) {
            // The API owns the rules it does not share with the form - the
            // owner gate, the charging-model condition - so its message is
            // shown rather than re-derived here.
            const e = err as { data?: { message?: string } };
            setFormError(e?.data?.message ?? t('organization.agreements.form.save_error'));
        }
    };

    const term = (agreement: SpaceAgreement): string => {
        const from = agreement.startDate ? formatDate(agreement.startDate, language) : null;
        const to = agreement.expiryDate ? formatDate(agreement.expiryDate, language) : null;
        if (!from && !to) return t('organization.agreements.open_ended');
        if (from && !to) return t('organization.agreements.from_open', { from });
        if (!from && to) return t('organization.agreements.until', { to });
        return `${from} – ${to}`;
    };

    if (accessLoading || agreementsLoading || !tenant) {
        return (
            <OrgDetailSection title={t('organization.sections.rooms')}>
                <p className={'text-sm text-foreground/60'}>{t('common.loading')}</p>
            </OrgDetailSection>
        );
    }

    // An error is NOT rendered as an empty list. "No rooms" and "we could not
    // find out" look identical on screen and the second one never gets
    // reported — the Seezeit failure mode (#1579).
    if (accessError || agreementsError) {
        return (
            <OrgDetailSection title={t('organization.sections.rooms')}>
                <p className={'text-sm text-foreground/60'}>
                    {t('organization.rooms.load_error')}
                </p>
            </OrgDetailSection>
        );
    }

    return (
        <div className={'flex flex-col gap-6'}>
            {/* Surfaced, never acted on (Amendment 5 addendum, decision 4): the
                room stays dedicated and members keep booking. Reverting it
                automatically would hand the room back to general availability
                on the strength of an expiry date nobody renewed in kolula. */}
            {needsAttention.length > 0 && (
                <div className={'flex items-start gap-2 rounded-[5px] bg-text-contrast shadow-sm px-5 py-4'}>
                    <AlertTriangle className={'h-4 w-4 mt-0.5 shrink-0 text-foreground/60'} />
                    <div className={'text-sm text-foreground/80'}>
                        <p className={'font-medium'}>{t('organization.agreements.ended_title')}</p>
                        <p className={'text-foreground/60'}>
                            {/* `total`, not `count`: `count` is i18next's
                                pluralization key and would send the lookup to
                                `ended_hint_one` / `_other`, which we do not
                                define — rendering the placeholder verbatim. */}
                            {t('organization.agreements.ended_hint', {
                                total: needsAttention.length,
                            })}
                        </p>
                    </div>
                </div>
            )}

            <OrgDetailSection
                title={t('organization.sections.rooms')}
                subtitle={t('organization.sections.rooms_subtitle')}
                help={t('organization.help.rooms')}
            >
                {assigning ? (
                    <div className={'mb-4'}>
                        <OrgRoomAssignmentEditor
                            organizationId={organizationId}
                            grants={grants}
                            agreements={agreements}
                            onClose={() => setAssigning(false)}
                        />
                    </div>
                ) : (
                    <div className={'mb-4'}>
                        <CustomButton
                            title={t('organization.rooms.assign.open')}
                            onClick={() => setAssigning(true)}
                            isFilled={false}
                        />
                    </div>
                )}

                {!assigning && grants.length === 0 && (
                    <p className={'text-sm text-foreground/60'}>{t('organization.rooms.empty')}</p>
                )}
                {!assigning && grants.length > 0 && (
                    <p className={'text-sm text-foreground/60 mb-2'}>{t('organization.rooms.listed_hint')}</p>
                )}
                {!assigning && grants.length > 0 && (
                    <div className={'overflow-x-auto'}>
                        <table className={'w-full text-sm'}>
                            <thead>
                                <tr className={'border-b border-foreground/5'}>
                                    <th className={tableHeaderCellClass}>
                                        {t('organization.rooms.columns.room')}
                                    </th>
                                    <th className={tableHeaderCellClass}>
                                        {t('organization.rooms.columns.access')}
                                    </th>
                                    <th className={tableHeaderCellClass}>
                                        {t('organization.rooms.columns.coverage')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {grants.map((grant) => (
                                    <tr
                                        key={grant.id}
                                        className={'border-b border-foreground/5 last:border-b-0'}
                                    >
                                        <td className={tableCellClass}>
                                            {/* Linked to the object page for the room
                                                itself; assignments are changed here,
                                                with "Räume zuordnen". Plain text until
                                                the endpoint returns `assetId`. */}
                                            {grant.assetId ? (
                                                <Link
                                                    to={makeAssetDetailsUrl(grant.assetId)}
                                                    className={'text-primary hover:underline'}
                                                >
                                                    {roomName(grant)}
                                                </Link>
                                            ) : (
                                                roomName(grant)
                                            )}
                                        </td>
                                        <td className={tableCellClass}>
                                            {t(`organization.rooms.mode.${grant.accessMode}`)}
                                        </td>
                                        <td className={tableCellClass}>
                                            {/* A dedicated room with no agreement is allowed
                                                (addendum decision 1) but incomplete — the room
                                                is held and nothing records on what terms, so
                                                its members pay until one is linked. */}
                                            {isDedicatedWithoutAgreement(grant) ? (
                                                <span className={'text-foreground/60 italic'}>
                                                    {t('organization.rooms.agreement_missing')}
                                                </span>
                                            ) : (
                                                coverageLabel(grant)
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </OrgDetailSection>

            {/* Allocation windows (ADR-0027), one unit per shared room and per open hall. Hidden
                while the assignment editor is open: the rooms are changing. */}
            {!assigning && <OrgAllocationWindows organizationId={organizationId} grants={grants} />}

            <OrgDetailSection
                title={t('organization.sections.agreements')}
                subtitle={t('organization.sections.agreements_subtitle')}
                help={t('organization.help.agreements')}
            >
                {/* Stated on every tenant (ADR-0008 amendment 2026-10-05,
                    decision 3): an agreement records that the organization
                    carries the usage, and kolula creates no invoice for it in
                    any invoice-issuer mode today. A tenant whose bookings
                    kolula invoices would otherwise reasonably assume this one
                    is invoiced too. */}
                <p className={'text-sm text-foreground/60 mb-4'}>
                    {t('organization.agreements.invoiced_outside_kolula')}
                </p>
                {formValue === null && (
                    <div className={'mb-4'}>
                        <CustomButton
                            title={t('organization.agreements.form.add')}
                            onClick={openCreate}
                            isFilled={false}
                        />
                    </div>
                )}

                {agreements.length === 0 ? (
                    <p className={'text-sm text-foreground/60'}>
                        {t('organization.agreements.empty')}
                    </p>
                ) : (
                    <div className={'overflow-x-auto'}>
                        <table className={'w-full text-sm'}>
                            <thead>
                                <tr className={'border-b border-foreground/5'}>
                                    <th className={tableHeaderCellClass}>
                                        {t('organization.agreements.columns.name')}
                                    </th>
                                    <th className={tableHeaderCellClass}>
                                        {t('organization.agreements.columns.number')}
                                    </th>
                                    <th className={tableHeaderCellClass}>
                                        {t('organization.agreements.columns.term')}
                                    </th>
                                    <th className={tableHeaderCellClass}>
                                        {t('organization.agreements.columns.status')}
                                    </th>
                                    <th className={tableHeaderCellClass}>
                                        {t('organization.agreements.columns.rooms')}
                                    </th>
                                    <th className={tableHeaderCellClass} />
                                </tr>
                            </thead>
                            <tbody>
                                {agreements.map((agreement) => {
                                    const endedState = agreementEndedState(agreement);
                                    // Every room linked to the contract — what it covers
                                    // under option C — not only the dedicated ones.
                                    const held = agreement.roomCount;
                                    return (
                                        <tr
                                            key={agreement.id}
                                            className={'border-b border-foreground/5 last:border-b-0'}
                                        >
                                            <td className={tableCellClass}>
                                                {agreement.name?.trim() || naLabel}
                                                {agreement.note?.trim() && (
                                                    <span className={'block text-xs text-foreground/60'}>
                                                        {agreement.note}
                                                    </span>
                                                )}
                                            </td>
                                            <td className={tableCellClass}>
                                                {agreement.agreementNumber?.trim() || naLabel}
                                            </td>
                                            <td className={tableCellClass}>{term(agreement)}</td>
                                            <td className={tableCellClass}>
                                                {t(`organization.agreements.status.${agreement.status}`)}
                                                {/* The status column alone would say "active"
                                                    on an agreement whose term ran out — nothing
                                                    writes `expired`. `hasEnded` is the server's
                                                    derived answer; `unknown` says so rather than
                                                    implying the term still runs. */}
                                                {endedState === 'ended' && agreement.status === 'active' && (
                                                    <span className={'block text-xs text-foreground/60'}>
                                                        {t('organization.agreements.term_over')}
                                                    </span>
                                                )}
                                                {endedState === 'unknown' && (
                                                    <span className={'block text-xs text-foreground/60 italic'}>
                                                        {t('organization.agreements.term_unknown')}
                                                    </span>
                                                )}
                                                {/* Notice given, term still running. The
                                                    status column says `active` and that is
                                                    correct - the customer is still paying -
                                                    so the decision is shown beside it rather
                                                    than written into the status, which is
                                                    terminal and would report the agreement
                                                    ended months early. */}
                                                {agreementLifecycleOf(agreement) === 'activeCanceled' && (
                                                    <span className={'block text-xs text-foreground/60'}>
                                                        {t('organization.agreements.under_notice')}
                                                    </span>
                                                )}
                                            </td>
                                            <td className={tableCellClass}>
                                                {held === undefined ? naLabel : held}
                                            </td>
                                            <td className={`${tableCellClass} text-right`}>
                                                {/* Both change something, so both sit in the
                                                    row's menu, the notice last (design-decisions.md
                                                    › Zeilenaktionen in Tabellen). An archived
                                                    agreement is withdrawn and gets no menu. */}
                                                <RowActionsMenu
                                                    rowName={agreement.name?.trim() || agreement.agreementNumber?.trim() || naLabel}
                                                    items={[{
                                                        // Editing stays available on an ENDED
                                                        // agreement: correcting the number or the
                                                        // note on a contract that has run out is
                                                        // ordinary record-keeping.
                                                        id: 'edit',
                                                        label: t('common.edit'),
                                                        onClick: () => openEdit(agreement),
                                                        hidden: agreement.archivedAt !== null,
                                                    }]}
                                                    destructiveItems={[{
                                                        // Only while the agreement is still running:
                                                        // an ended one has nothing left to give
                                                        // notice on.
                                                        id: 'cancel',
                                                        label: t('organization.agreements.cancel'),
                                                        onClick: () => openCancel(agreement),
                                                        hidden: endedState !== 'running' || agreement.archivedAt !== null,
                                                    }]}
                                                />
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </OrgDetailSection>

            {/* #2165 3 - record or correct a contract concluded elsewhere.
                Rendered below the section rather than in a modal, matching the
                cancel panel: both are one-at-a-time and both need the table
                above them to stay readable while they are open. */}
            {formValue !== null && (
                <OrgAgreementForm
                    value={formValue}
                    onChange={(updates) => setFormValue(
                        (prev) => (prev ? { ...prev, ...updates } : prev),
                    )}
                    onSubmit={submitForm}
                    onCancel={closeForm}
                    isEdit={editTarget !== null}
                    isSaving={isCreating || isUpdating}
                    error={formError}
                    showValidation={showValidation}
                />
            )}

            {/* #2165 4.1 - notice, with an end date. Deliberately NOT a
                confirm-only prompt: an operator cancelling a contract STATES
                when it ends, and a blank date cannot be told apart from
                "nobody filled it in" - which is exactly what makes a stale
                date dangerous. The rules themselves (today allowed, the past
                refused, before the start refused) belong to the API; this
                collects the date and renders what it says. */}
            {cancelTarget && (
                <div className={'flex flex-col gap-3 rounded-[5px] bg-text-contrast shadow-sm px-5 py-4'}>
                    <div>
                        <p className={'text-sm font-medium text-foreground'}>
                            {t('organization.agreements.cancel_title', {
                                name: cancelTarget.name?.trim() || naLabel,
                            })}
                        </p>
                        <p className={'text-sm text-foreground/60'}>
                            {t('organization.agreements.cancel_hint', {
                                total: cancelTarget.roomCount ?? 0,
                            })}
                        </p>
                    </div>

                    {cancelError && (
                        <p className={'text-sm text-foreground'}>{cancelError}</p>
                    )}

                    <div className={'flex flex-col gap-2'}>
                        <label
                            htmlFor={'agreement-end-date'}
                            className={'text-sm font-medium text-foreground/80'}
                        >
                            {t('organization.agreements.cancel_end_date')}
                        </label>
                        <input
                            id={'agreement-end-date'}
                            type={'date'}
                            value={endDate}
                            onChange={(event) => setEndDate(event.target.value)}
                            className={'w-56 rounded-md border border-foreground/15 px-3 py-2 text-sm '
                                + 'bg-text-contrast text-foreground focus:outline-none focus:border-foreground/40'}
                        />
                    </div>

                    <div className={'flex gap-3'}>
                        <CustomButton
                            title={t('common.cancel')}
                            onClick={() => setCancelTarget(null)}
                            isFilled={false}
                        />
                        <CustomButton
                            title={t('organization.agreements.cancel_confirm')}
                            onClick={submitCancel}
                            loading={isCancelling}
                            disabled={endDate.trim().length === 0}
                            variant={'delete'}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default OrgRoomAccessSection;
